import React, {MutableRefObject, UIEvent} from 'react';
import {observer} from 'mobx-react-lite';
import classNames from "classnames";
import { useRootStore, useServiceRegistry, useAppState, useDsManager } from "@stores/AppStateContext";
import { useCrudServices } from "../../../platform/hooks/useServiceCombinations";

import {SearchResults} from '../SearchResults';
import {Recent} from '../Recent';
import {Active} from '../Active';
import {getDebounceUpdateScrollShiftHandle} from "../context-menu/zIndexLimitationWorkaround";
import {useDropArea} from "../../hooks/useDropArea";
import {SharedDynamicViewVariation} from "./SharedDynamicViewVariation";
import {TreeDummyLoading} from "../../components/TreeDummyLoading";
import {documentImport} from "../../../actions/import/documentsImport";

import {inAppEventsHub} from "../../../volatile/InAppEventsHub";
import {screenSizeProvider} from "../../../stores/ScreenSizeProvider";
import {snackBarRef} from "../../../stores/snackbarRef";
import {supportedDocumentTypes} from "../../../actions/import/importFile";

import './list-view.css';

export class ListViewPure extends React.PureComponent {
    render() {
        return <ListView/>
    }
}

export const leftMenuContextRootRef: MutableRefObject<HTMLDivElement | null> = {
    current: null
};

export const leftMenuScrollRootRef: MutableRefObject<HTMLDivElement | null> = {
    current: null
};

export const ListView = observer(() => {
    const rootStore = useRootStore();
    const appState = useAppState();
    const serviceRegistry = useServiceRegistry();
    const storageService = serviceRegistry.getStorageService();
    const dsManager = useDsManager();
    const crudServices = useCrudServices();
    
    const enqueueSnackbar = snackBarRef.current!.enqueueSnackbar;
    const doImport = (files: FileList) => {
        const workspace = rootStore.view.workspace;
        if (!workspace || !dsManager) {
            console.error('Workspace or data source manager is not ready for import');
            return;
        }
        documentImport(
            {
                ...crudServices,
                workspace,
                rootStore,
                appState,
            },
            files,
            enqueueSnackbar,
            storageService,
            appState.userState!.tenantId
        );
    };

    const {isDragOver, ...dragHandles} = useDropArea(doImport, supportedDocumentTypes);
    return (
        <div
            className={classNames({
                "list-view": true,
                "list-view--dragover": isDragOver,
            })}
            ref={leftMenuScrollRootRef}
            onScroll={listViewScrollHandle}
            {...dragHandles}
        >
            {rootStore.view.workspace?.isReady
                ? rootStore.search.isEmpty
                    ? <WorkspaceView view={rootStore.view.workspace!.view}/>
                    : <SearchResults/>
                : <TreeDummyLoading/>
            }
        </div>
    );
});

interface WorkspaceViewProps {
    view: string;
}

const WorkspaceView = ({view}: WorkspaceViewProps) => {
    switch (view) {
        case 'recent':
            return <Recent/>;
        case 'active':
            return <Active/>;
        default:
            return <SharedDynamicViewVariation/>
    }
}

const debounceUpdateScrollShift = getDebounceUpdateScrollShiftHandle(leftMenuContextRootRef, leftMenuScrollRootRef);

let lastScrollTop = 0;
const listViewScrollHandle = (event: UIEvent) => {
    if (screenSizeProvider.view !== 'desktop') {
        requestAnimationFrame(() => {
            // @ts-ignore
            const currentScroll = event.target.scrollTop;
            if (currentScroll > lastScrollTop) {
                inAppEventsHub.dispatch({
                    type: 'TreeScroll',
                    scrollDirection: 'down'
                })
            } else if (currentScroll < lastScrollTop) {
                inAppEventsHub.dispatch({
                    type: 'TreeScroll',
                    scrollDirection: 'up'
                })
            }
            lastScrollTop = currentScroll;
        })
    }
    debounceUpdateScrollShift();
}
